import { Spinner } from './ui/Icon.js';
import type { CollectionPreview } from '../types/index.js';
import { ENGINE_LABELS } from '../types/index.js';

interface CostPreviewProps {
  preview: CollectionPreview | null;
  loading: boolean;
  /** When provided, shows single-brand summary instead of multi-brand breakdown */
  brandName?: string;
}

export function CostPreview({ preview, loading, brandName }: CostPreviewProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner className="text-violet-500" />
      </div>
    );
  }

  if (!preview) return null;

  const engineCount = preview.engines.length;

  // Derive template count: sum of per-brand counts, or fallback to templates array length
  const templateCount = preview.brands.reduce((sum, b) => {
    const tc = (b as Record<string, unknown>).template_count as number | undefined;
    return sum + (tc ?? 0);
  }, 0) || preview.templates.length;

  // Region dimension — when multi-region capture is active, explain how the
  // (now accurate) total_queries splits across regions. Hidden otherwise.
  const regionSplit = preview.region_capture_active
    ? Object.entries(preview.per_region_queries ?? {}).sort(([a], [b]) => a.localeCompare(b))
    : [];

  return (
    <div className="p-5" data-testid="cost-preview">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          {brandName ? (
            <p className="text-sm text-slate-600">
              Next run will execute{' '}
              <span className="font-semibold text-slate-900">{preview.total_queries} queries</span>
              {' '}&mdash; {templateCount} prompts &times; {engineCount} engines &times;{' '}
              {preview.samples_per_combo} sample{preview.samples_per_combo !== 1 ? 's' : ''} for{' '}
              <span className="font-medium text-slate-700">{brandName}</span>
            </p>
          ) : (
            <>
              <p className="text-sm text-slate-600">
                Next run will execute{' '}
                <span className="font-semibold text-slate-900">{preview.total_queries} queries</span>
                {' '}across {preview.brands.length} brand{preview.brands.length !== 1 ? 's' : ''} &times; {engineCount} engines
              </p>

              {/* Per-brand prompt counts */}
              <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-500">
                {preview.brands.map((brand) => {
                  const tc = (brand as Record<string, unknown>).template_count as number | undefined;
                  return (
                    <span key={brand.id}>
                      <span className="font-medium text-slate-700">{brand.name}</span>: {tc ?? '?'} prompts &times; {engineCount} engines &times; {preview.samples_per_combo} sample{preview.samples_per_combo !== 1 ? 's' : ''}
                    </span>
                  );
                })}
              </div>
            </>
          )}

          {/* Region split — only when multi-region capture is active */}
          {regionSplit.length > 0 && (
            <p className="mt-1.5 text-xs text-slate-500" data-testid="cost-preview-regions">
              Across {regionSplit.length} region{regionSplit.length !== 1 ? 's' : ''}
              {regionSplit.map(([code, count]) => (
                <span key={code}>
                  <span className="text-slate-300"> · </span>
                  <span className="font-medium text-slate-700">{code}</span> {count}
                </span>
              ))}
            </p>
          )}

          {/* Cost breakdown. Spec 250 (review P1-15): an engine's cost is a
              category and a number, not a status, so these are Kridt Tags —
              lined, never filled. They were grey-filled chips. */}
          <div className="mt-2 flex flex-wrap gap-1.5" data-testid="cost-breakdown">
            {preview.engines.map((engine) => (
              // Spec 234: engine colour is retired — an engine is an identity,
              // not a status, and its identity is its name.
              <span key={engine} className="hub-tag">
                {ENGINE_LABELS[engine] ?? engine}: ${(preview.cost_breakdown[engine] ?? 0).toFixed(2)}
              </span>
            ))}
          </div>
        </div>

        <div className="text-right flex-shrink-0">
          {/* Spec 250 (review P1-15): a figure (28/500) in ink, as every figure
              is — it was 24/700 in Skagen. */}
          <p className="hub-stat__value" data-testid="cost-estimate">${preview.estimated_cost.toFixed(2)}</p>
          <p className="text-xs text-[var(--hub-color-ink-muted)]">estimated cost</p>
        </div>
      </div>
    </div>
  );
}
